<template>
  <div class="app-shell" :class="{ 'sidebar-open': sidebarOpen }">
    <button
      v-if="sidebarOpen"
      class="overlay"
      type="button"
      :aria-label="$t('memberLayout.actions.closeMenu')"
      @click="sidebarOpen = false"
    ></button>

    <aside class="sidebar border-end">
      <div class="sidebar-header d-flex align-items-center gap-2 px-3">
        <div class="brand-icon">
          <i class="bi bi-piggy-bank"></i>
        </div>

        <div class="flex-grow-1">
          <div class="brand-title">Saving System</div>
          <div class="brand-subtitle">{{ $t("memberLayout.workspace") }}</div>
        </div>

        <button
          class="btn btn-sm btn-outline-secondary d-lg-none"
          @click="sidebarOpen = false"
          :aria-label="$t('memberLayout.actions.closeMenu')"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <nav class="nav flex-column px-2 mt-2 sidebar-nav">
        <template v-for="(sec, i) in filteredMenu" :key="i">
          <div
            v-if="sec.section"
            class="px-2 mt-3 mb-2 text-uppercase small text-muted fw-semibold"
          >
            {{ translateMenuSection(sec) }}
          </div>

          <template
            v-for="item in sec.items"
            :key="item.key || item.to || item.label"
          >
            <!-- Normal item -->
            <RouterLink
              v-if="!item.children || !item.children.length"
              :to="item.to"
              class="nav-link"
              active-class="active"
              exact-active-class="active"
            >
              <i :class="`bi ${item.icon} me-2`"></i>
              <span>{{ translateMenuItem(item) }}</span>
            </RouterLink>

            <!-- Parent with submenu -->
            <div v-else class="submenu-wrap">
              <button
                type="button"
                class="nav-link nav-link-btn w-100"
                :class="{ active: isParentActive(item) }"
                :aria-expanded="isSubmenuOpen(item)"
                @click="toggleSubmenu(item)"
              >
                <span class="d-flex align-items-center">
                  <i :class="`bi ${item.icon} me-2`"></i>
                  <span>{{ translateMenuItem(item) }}</span>
                </span>

                <i
                  class="bi submenu-caret"
                  :class="
                    isSubmenuOpen(item) ? 'bi-chevron-up' : 'bi-chevron-down'
                  "
                ></i>
              </button>

              <div v-show="isSubmenuOpen(item)" class="submenu-list">
                <RouterLink
                  v-for="child in item.children"
                  :key="child.to"
                  :to="child.to"
                  class="nav-sublink"
                  active-class="active"
                  exact-active-class="active"
                >
                  <i :class="`bi ${child.icon || 'bi-dot'} me-2`"></i>
                  <span>{{ translateMenuItem(child) }}</span>
                </RouterLink>
              </div>
            </div>
          </template>
        </template>

        <!-- Admin entry point -->
        <div
          v-if="isAdmin"
          class="px-2 mt-3 mb-2 text-uppercase small text-muted fw-semibold"
        >
          {{ $t("memberLayout.admin.section") }}
        </div>

        <RouterLink
          v-if="isAdmin"
          to="/admin"
          class="nav-link"
          active-class="active"
          exact-active-class="active"
        >
          <i class="bi bi-shield-lock me-2"></i>
          <span>{{ $t("memberLayout.admin.panel") }}</span>
        </RouterLink>
      </nav>

      <div class="sidebar-footer mt-auto px-3 py-3 border-top">
        <div class="d-flex align-items-center gap-2">
          <div class="avatar">{{ initials }}</div>

          <div class="flex-grow-1">
            <div class="fw-semibold">
              {{ user?.name || $t("memberLayout.userFallback") }}
            </div>
            <div class="text-muted small">
              {{ formattedGroupRole }} · {{ activeGroupName }}
            </div>
          </div>

          <button
            class="btn btn-sm btn-outline-secondary"
            @click="doLogout"
            :title="$t('memberLayout.actions.logout')"
          >
            <i class="bi bi-box-arrow-right"></i>
          </button>
        </div>
      </div>
    </aside>

    <div class="main">
      <header class="topbar border-bottom bg-white">
        <div
          class="d-flex align-items-center justify-content-between px-3"
          style="height: 64px"
        >
          <div class="d-flex align-items-center gap-2 gap-md-3">
            <button
              class="btn btn-sm btn-outline-secondary d-inline-flex d-lg-none"
              @click="sidebarOpen = true"
              :aria-label="$t('memberLayout.actions.openMenu')"
            >
              <i class="bi bi-list"></i>
            </button>

            <div class="topbar-identity">
              <div class="fw-semibold text-dark text-truncate">
                {{
                  $t("memberLayout.welcome", {
                    name: user?.name || "...",
                  })
                }}
              </div>
              <div class="topbar-context text-truncate">
                {{ activeGroupName }} - {{ $t("memberLayout.roleLabel") }}:
                {{ formattedGroupRole }}
              </div>
            </div>
          </div>

          <div class="d-flex align-items-center gap-2">
            <LanguageSwitcher />

            <div class="topbar-chip d-none d-md-inline-flex align-items-center">
              <i class="bi bi-shield-lock me-1"></i>
              {{ $t("memberLayout.secureOtp") }}
            </div>
          </div>
        </div>
      </header>

      <main class="content p-3 p-md-4">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import { MENU_MEMBER } from "@/config/menu";
import { canAccess } from "@/utils/acl";
import { getUser, logout } from "@/services/auth";
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";

export default {
  name: "MemberLayout",

  components: {
    LanguageSwitcher,
  },

  data() {
    return {
      user: getUser(),
      MENU: MENU_MEMBER,
      sidebarOpen: false,
      openSubmenus: {},
    };
  },

  computed: {
    activeGroupId() {
      return localStorage.getItem("active_group_id");
    },

    activeGroupName() {
      return (
        localStorage.getItem("active_group_name") ||
        this.$t("memberLayout.selectedGroup")
      );
    },

    activeGroupRole() {
      return localStorage.getItem("active_group_role") || "member";
    },

    formattedGroupRole() {
      return this.formatRole(this.activeGroupRole);
    },

    menuUser() {
      return {
        ...(this.user || {}),
        role: this.activeGroupRole,
      };
    },

    filteredMenu() {
      const u = this.menuUser;

      return this.MENU.filter((sec) => canAccess(u, sec.roles || []))
        .map((sec) => ({
          ...sec,
          items: (sec.items || [])
            .map((item) => {
              const children = (item.children || []).filter((child) =>
                canAccess(u, child.roles || []),
              );

              const allowedSelf =
                !item.roles ||
                item.roles.length === 0 ||
                canAccess(u, item.roles || []);

              if (!allowedSelf && children.length === 0) return null;

              return {
                ...item,
                children,
              };
            })
            .filter(Boolean)
            .filter((item) => {
              if (item.children && item.children.length > 0) return true;
              return !!item.to;
            }),
        }))
        .filter((sec) => sec.items.length > 0);
    },

    initials() {
      const n = this.user?.name || "";
      const parts = n.trim().split(" ").filter(Boolean);
      const a = parts[0]?.[0] || "U";
      const b = parts[1]?.[0] || "";
      return (a + b).toUpperCase();
    },

    isAdmin() {
      return ["admin", "treasurer"].includes(this.activeGroupRole);
    },
  },

  watch: {
    $route() {
      this.user = getUser();
      this.sidebarOpen = false;
      this.expandActiveParents();
    },
  },

  mounted() {
    this.user = getUser();
    this.expandActiveParents();
    window.addEventListener("storage", this.syncSessionContext);
    window.addEventListener("keydown", this.handleEscape);
  },

  beforeUnmount() {
    window.removeEventListener("storage", this.syncSessionContext);
    window.removeEventListener("keydown", this.handleEscape);
  },

  methods: {
    formatRole(role) {
      const normalizedRole = String(role || "member")
        .toLowerCase()
        .replaceAll("_", "");

      const keys = {
        owner: "memberLayout.roles.owner",
        admin: "memberLayout.roles.admin",
        treasurer: "memberLayout.roles.treasurer",
        member: "memberLayout.roles.member",
      };

      return this.$t(keys[normalizedRole] || "memberLayout.roles.member");
    },

    normalizeMenuKey(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    },

    translateMenuSection(section) {
      const key = section?.key;

      if (!key) {
        return section?.section || "";
      }

      const translationKey = `menu.sections.${key}`;

      return this.$te(translationKey)
        ? this.$t(translationKey)
        : section?.section || key;
    },

    translateMenuItem(item) {
      const key = item?.key;

      if (!key) {
        return item?.label || "";
      }

      const translationKey = `menu.items.${key}`;

      return this.$te(translationKey)
        ? this.$t(translationKey)
        : item?.label || key;
    },

    submenuKey(item) {
      return item.key || item.to || item.label;
    },

    isSubmenuOpen(item) {
      return !!this.openSubmenus[this.submenuKey(item)];
    },

    toggleSubmenu(item) {
      const key = this.submenuKey(item);
      this.openSubmenus = {
        ...this.openSubmenus,
        [key]: !this.openSubmenus[key],
      };
    },

    isParentActive(item) {
      if (!item.children || !item.children.length) return false;
      return item.children.some((child) =>
        this.$route.path.startsWith(child.to),
      );
    },

    expandActiveParents() {
      const next = { ...this.openSubmenus };

      this.filteredMenu.forEach((sec) => {
        (sec.items || []).forEach((item) => {
          if (item.children && item.children.length) {
            const key = this.submenuKey(item);

            if (
              item.children.some((child) =>
                this.$route.path.startsWith(child.to),
              )
            ) {
              next[key] = true;
            }
          }
        });
      });

      this.openSubmenus = next;
    },

    syncSessionContext() {
      this.user = getUser();
    },

    handleEscape(event) {
      if (event.key === "Escape") {
        this.sidebarOpen = false;
      }
    },

    async doLogout() {
      await logout();
      this.$router.replace("/login/sms");
    },
  },
};
</script>

<style scoped>
.app-shell {
  --sidebar-bg: #111827;
  --sidebar-bg-soft: #1f2937;
  --sidebar-text: #d1d5db;
  --sidebar-muted: #94a3b8;
  --sidebar-active: #ffffff;
  --sidebar-accent: #3b82f6;
  --sidebar-hover: rgba(255, 255, 255, 0.07);
  --sidebar-border: rgba(255, 255, 255, 0.08);

  display: grid;
  grid-template-columns: 248px minmax(0, 1fr);
  min-height: 100vh;
  min-width: 0;
  background: #f8fafc;
}

.overlay {
  display: none;
  padding: 0;
  border: 0;
}

.sidebar {
  position: sticky;
  top: 0;
  min-height: 100vh;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, var(--sidebar-bg), #0f172a);
  color: var(--sidebar-text);
  border-right: 1px solid rgba(15, 23, 42, 0.18) !important;
  box-shadow: 8px 0 24px rgba(15, 23, 42, 0.06);
}

.sidebar-header {
  min-height: 76px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--sidebar-border);
}

.brand-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(59, 130, 246, 0.15);
  border: 1px solid rgba(96, 165, 250, 0.22);
  color: #60a5fa;
  font-size: 20px;
  flex-shrink: 0;
}

.brand-icon i {
  font-size: 20px;
  color: inherit;
}

.brand-title {
  color: #fff;
  font-size: 0.98rem;
  font-weight: 800;
  line-height: 1.15;
}

.brand-subtitle {
  margin-top: 3px;
  color: var(--sidebar-muted);
  font-size: 0.75rem;
  line-height: 1.1;
}

.sidebar-nav {
  flex: 1;
  padding: 8px 8px 16px !important;
}

.sidebar .text-muted {
  color: var(--sidebar-muted) !important;
}

.sidebar nav > .text-uppercase,
.sidebar nav template > .text-uppercase {
  padding: 0 10px !important;
  margin-top: 18px !important;
  margin-bottom: 7px !important;
  font-size: 0.67rem !important;
  letter-spacing: 0.1em;
}

.nav-link,
.nav-link-btn,
.nav-link-group {
  min-height: 38px;
  border-radius: 9px;
  color: var(--sidebar-text);
  padding: 8px 11px;
  margin: 2px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 500;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.nav-link i,
.nav-link-btn i,
.nav-link-group i {
  width: 18px;
  text-align: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.nav-link:hover,
.nav-link-btn:hover,
.nav-link-group:hover {
  background: var(--sidebar-hover);
  color: #fff;
  transform: none;
}

.nav-link.active,
.nav-link-btn.active,
.nav-link-group.active {
  position: relative;
  background: rgba(59, 130, 246, 0.16);
  color: var(--sidebar-active);
  font-weight: 700;
  box-shadow: none;
}

.nav-link.active::before,
.nav-link-btn.active::before,
.nav-link-group.active::before {
  content: "";
  position: absolute;
  left: -8px;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 999px;
  background: var(--sidebar-accent);
}

.nav-link-btn,
.nav-link-group {
  width: 100%;
  justify-content: space-between;
  border: 0;
  background: transparent;
}

.submenu-wrap,
.nav-group {
  margin: 0;
}

.submenu-list,
.submenu {
  margin: 1px 0 4px 16px;
  padding-left: 10px;
  border-left: 1px solid rgba(148, 163, 184, 0.2);
}

.nav-sublink {
  min-height: 32px;
  margin: 1px 0;
  padding: 7px 9px;
  border-radius: 8px;
  background: transparent;
  color: #aeb8c7;
  font-size: 0.85rem;
  font-weight: 500;
}

.nav-sublink:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  transform: none;
}

.nav-sublink.active {
  background: rgba(59, 130, 246, 0.13);
  color: #fff;
  font-weight: 700;
}

.submenu-caret,
.submenu-chevron {
  width: auto !important;
  font-size: 0.72rem !important;
  color: var(--sidebar-muted);
}

.sidebar-footer {
  flex-shrink: 0;
  padding: 14px 16px !important;
  background: rgba(15, 23, 42, 0.45);
  border-top: 1px solid var(--sidebar-border) !important;
}

.sidebar-footer .fw-semibold {
  color: #fff;
  font-size: 0.88rem;
  line-height: 1.2;
}

.sidebar-footer .small {
  margin-top: 3px;
  color: var(--sidebar-muted) !important;
  font-size: 0.72rem;
  line-height: 1.2;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: rgba(59, 130, 246, 0.18);
  border: 1px solid rgba(96, 165, 250, 0.22);
  color: #dbeafe;
  font-size: 0.82rem;
  font-weight: 800;
  flex-shrink: 0;
}

.sidebar .btn-outline-secondary {
  width: 34px;
  height: 34px;
  padding: 0;
  display: inline-grid;
  place-items: center;
  color: var(--sidebar-muted);
  background: transparent;
  border-color: var(--sidebar-border);
  border-radius: 9px;
}

.sidebar .btn-outline-secondary:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.14);
}

.main {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 1030;
  border-bottom: 1px solid #e2e8f0 !important;
  background: rgba(255, 255, 255, 0.96) !important;
  color: #0f172a;
  backdrop-filter: blur(10px);
}

.topbar .text-dark {
  color: #0f172a !important;
}

.topbar-context {
  color: #64748b;
}

.topbar :deep(.language-switcher) {
  flex-shrink: 0;
}

.topbar-chip {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #334155;
}

.topbar .btn-outline-secondary {
  color: #475569;
  border-color: #cbd5e1;
}

.topbar .btn-outline-secondary:hover {
  color: #0f172a;
  background: #f8fafc;
  border-color: #94a3b8;
}

.content {
  min-width: 0;
  min-height: calc(100vh - 64px);
  overflow-x: hidden;
}

@media (max-width: 991.98px) {
  .app-shell {
    grid-template-columns: 1fr;
  }

  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    overflow-y: auto;
    width: 248px;
    z-index: 1055;
    transform: translateX(-102%);
    transition: transform 0.2s ease;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }

  .app-shell.sidebar-open .sidebar {
    transform: translateX(0);
  }

  .overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    z-index: 1050;
    display: none;
  }

  .app-shell.sidebar-open .overlay {
    display: block;
  }
}
@media (max-width: 575.98px) {
  .topbar-identity {
    max-width: calc(100vw - 92px);
  }

  .content {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
}
</style>
